import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useClient, useQuery } from "jsonapi-react";
import { API_BASE_URL, VW_ROUTES, apiRoutes } from "../../constants";
import { setSession, parseJwt, getSession } from "../../helpers/utils";
import { parseAPIResponseError, validatorModule } from "../../helpers/helpers";
import loginImage from "../../assets/images/login_img.png";
import logowhite from "../../assets/images/logo_w.png";
import logo from "../../assets/images/logo.png";
import { LuEye as EyeOpen } from "react-icons/lu";
import { LuEyeOff as EyeClose } from "react-icons/lu";
import Maintenance from "../Maintenance/Maintenance";
import useCheckMaintenance from "../../hooks/useCheckMaintenance/useCheckMaintenance";
import _ from "lodash";
import { useToasts } from "react-toast-notifications";
import axios from "axios";

const validationFormat = [
  {
    field: "email",
    isEmail: true,
    isRequired: true,
    fieldName: "Email",
  },
  {
    field: "password",
    isRequired: true,
    fieldName: "Password",
  },
];

const ErrorToastWithSideTimer = ({
  children,
  onDismiss,
  closeToast,
  initialSeconds,
  onTimerComplete, // New prop to notify when timer completes
}) => {
  const [timeLeft, setTimeLeft] = useState(initialSeconds);

  useEffect(() => {
    if (timeLeft <= 0) {
      onDismiss?.();
      closeToast?.();
      onTimerComplete?.(); // Notify parent component
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, onDismiss, closeToast, onTimerComplete]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressPercentage = (timeLeft / initialSeconds) * 100;
  const strokeDasharray = 2 * Math.PI * 20;
  const strokeDashoffset =
    strokeDasharray - (progressPercentage / 100) * strokeDasharray;

  return (
    <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 shadow-sm error-alert">
      <div className="flex-fill text-left pr-4">
        <div className="d-flex align-items-center">
          <div className="mr-2 account-lock-icon">!</div>
          <h6 className="text-danger font-weight-bold mb-0 error-title">
            Account Locked
          </h6>
        </div>
        <div className="text-danger error-text mt-1">{children}</div>
      </div>

      <div className="flex-shrink-0 text-right ml-auto">
        <div className="position-relative">
          <svg className="timer-svg" width="48" height="48" viewBox="0 0 50 50">
            <circle
              cx="25"
              cy="25"
              r="20"
              stroke="#fecaca"
              strokeWidth="4"
              fill="none"
            />
            <circle
              cx="25"
              cy="25"
              r="20"
              stroke="#ef4444"
              strokeWidth="4"
              fill="none"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="timer-circle-animated"
            />
          </svg>
          <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center timer-overlay">
            <span className="text-danger font-weight-bold timer-time-text">
              {formatTime(timeLeft)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Login = () => {
  const initialState = {
    email: "",
    password: "",
  };
  const history = useHistory();
  const { addToast, removeAllToasts } = useToasts();

  const { isMaintenance, loading, systemInfo } = useCheckMaintenance();
  const [formState, updateFormState] = React.useState(initialState);
  const [error, updateErrorMessage] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [showPassWord, setShowPassword] = React.useState(false);
  const [isLockedOut, setIsLockedOut] = React.useState(false); // New state to track lockout

  const client = useClient();
  client.config.headers = {};

  const [logInUser] = useMutation(apiRoutes.login, { client });

  React.useEffect(() => {
    const user = getSession();
    if (user) {
      history.push(VW_ROUTES.DASHBOARD);
    }
  }, [history]);

  const handleShowPassword = () => {
    setShowPassword((currState) => !currState);
  };

  const onInputChangeEvent = (e) => {
    updateFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const handleTimerComplete = () => {
    setIsLockedOut(false);
  };

  const doLoginUser = async (event) => {
    event.preventDefault();
    removeAllToasts();

    const localErrors = validatorModule(formState, validationFormat);
    if (Object.keys(localErrors).length > 0) {
      updateErrorMessage(localErrors);
      return;
    }
    setIsLoading(true);
    const data = {
      data: {
        type: "auth",
        attributes: {
          email: formState.email,
          password: formState.password,
        },
      },
    };
    axios
      .post(`${API_BASE_URL}/${apiRoutes.login}`, data)
      .then((res) => {
        setIsLoading(false);

        let response = res?.data?.data?.attributes;
        const resp = parseJwt(response.token);
        setSession(response);
        if (resp.dashboard === "administrator") {
          history.push(VW_ROUTES.USER_MANAGEMENT);
        } else if (
          resp.dashboard === "ac_dashboard" &&
          resp.role_slug === "account-coordinator"
        ) {
          history.push(VW_ROUTES.APPLICANTS_HISTORY);
        } else {
          history.push(VW_ROUTES.DASHBOARD);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        if (
          error?.response?.data?.errors[1]?.is_locked?.toLowerCase() === "true"
        ) {
          setIsLockedOut(true);
          const lockoutDuration = error?.response?.data?.errors[1]?.expires_in;
          addToast(error?.response?.data?.errors?.[0]?.detail, {
            appearance: "error",
            autoDismiss: false,
            autoDismissTimeout: 0,
            component: (props) => (
              <ErrorToastWithSideTimer
                {...props}
                initialSeconds={parseInt(lockoutDuration)}
                onTimerComplete={handleTimerComplete}
              />
            ),
          });
        } else {
          addToast(
            error?.response?.data?.errors?.[0]?.detail ??
              "Something went wrong",
            {
              appearance: "error",
              autoDismiss: true,
            }
          );
        }
      });
  };

  if (!(isMaintenance === false)) {
    return <Maintenance isLoading={loading} isMaintenance={isMaintenance} />;
  }

  return (
    <>
      <main className="login-cover">
        <div className="left-section">
          <div className="content-portion">
            <div className="logo_w">
              <img src={logowhite} alt="v-watch" />
            </div>
            <p>
              Vwatch a revolutionary solution for enterprises to become hybrid
              workplaces. We offer a comprehensive suite that will help you
              automate work with AI and optimise your space in alignment with
              the hybrid needs of your workforce.
            </p>
            <p>
              Smart Solutions for smarter places. Delight your workforce with
              features designed to make their workplace experiences more
              seamless.
            </p>
          </div>
          <div className="image-holder">
            <img src={loginImage} alt="v-watch" />
          </div>
        </div>
        <div className="right-section">
          <div className="content-portion">
            <div className="content-inner">
              <div className="logo">
                <img src={systemInfo?.site?.logo} alt="v-watch" />
              </div>
              <h2>Login to Your Account</h2>
              <form onSubmit={doLoginUser}>
                <label className="form-group has-float-label mb-4">
                  <input
                    onChange={(e) => onInputChangeEvent(e)}
                    name="email"
                    placeholder="sample@domain.com"
                    type="text"
                    className="form-control m-0"
                  />
                  <span>E-mail</span>
                  <p
                    className="login-error-message"
                    style={{
                      marginBottom:
                        error &&
                        (String(error?.email ?? "").length ||
                          (error?.field === "email" &&
                            String(error?.errorMsg ?? "").length))
                          ? "-1.3rem"
                          : "0",
                    }}
                  >
                    {String(error?.email ?? "").length ? error?.email : ""}
                    {error && error?.field === "email" ? error.errorMsg : ""}
                  </p>
                </label>

                <label className="form-group has-float-label mb-4">
                  <div className="position-relative">
                    <input
                      onChange={(e) => onInputChangeEvent(e)}
                      name="password"
                      className="form-control m-0"
                      type={showPassWord ? "text" : "password"}
                      placeholder=""
                    />
                    <span
                      className="input-tail-icon pb-0"
                      onClick={handleShowPassword}
                      style={{
                        pointerEvents: isLockedOut ? "none" : "auto",
                        opacity: isLockedOut ? 0.5 : 1,
                      }}
                    >
                      {showPassWord ? <EyeOpen /> : <EyeClose />}
                    </span>
                  </div>
                  <span>Password</span>
                  <p
                    className="login-error-message"
                    style={{
                      marginBottom:
                        error &&
                        (String(error?.password ?? "").length ||
                          (error?.field === "password" &&
                            String(error?.errorMsg ?? "").length))
                          ? "-1.3rem"
                          : "0",
                    }}
                  >
                    {String(error?.password ?? "").length
                      ? error?.password
                      : ""}
                    {error && error?.field === "password" ? error.errorMsg : ""}
                  </p>
                </label>
                <div className="d-flex justify-content-between align-items-center">
                  <a href={`#${VW_ROUTES.FORGOT_PASSWORD}`}>Forgot password?</a>
                  <button
                    className="btn btn-secondary btn-lg btn-shadow position-relative"
                    type="submit"
                    disabled={isLoading}
                  >
                    LOGIN{" "}
                    {isLoading && (
                      <span className="button-loader-container">
                        <div
                          className="spinner-border spinner-border-sm ml-1 text-white"
                          role="status"
                        ></div>
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Login;
